import type {
  AccountNode,
  Loan,
  LoanAccrualProjection,
  LoanDrawdown,
  LoanPayment,
  RecordLoanDrawdownInput,
  RecordLoanPaymentInput,
} from '@taler/api-client'
import { accountKeys, entryKeys, loanKeys } from '@taler/api-client'
import { formatLocalDate, type Locale, plusMonths } from '@taler/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { isNotFound } from '@/app/not-found'
import { problemDetail } from '../internal/problem.ts'
import { paymentRowsFromEntries, type LoanPaymentRow } from '../internal/payment-history.ts'
import { flattenSourceAccounts, type SourceAccountOption } from '../internal/source-accounts.ts'

export interface UseLoanOptions {
  readonly ledgerId: () => string | undefined
  readonly loanId: () => string
  readonly enabled: () => boolean
  /** How far past the loan's next accrual date the projection runs. */
  readonly projectionMonths: () => number
  readonly locale: () => Locale
}

export interface UseLoan {
  readonly loan: ComputedRef<Loan | undefined>
  readonly payments: ComputedRef<readonly LoanPaymentRow[]>
  readonly hasHistory: ComputedRef<boolean | undefined>
  readonly projections: ComputedRef<readonly LoanAccrualProjection[]>
  readonly drawdownAccounts: ComputedRef<readonly SourceAccountOption[]>
  readonly sourceAccounts: ComputedRef<readonly SourceAccountOption[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly notFound: ComputedRef<boolean>
  readonly retrying: ComputedRef<boolean>
  readonly isRecordingDrawdown: ComputedRef<boolean>
  readonly drawdownErrorDetail: ComputedRef<string | undefined>
  readonly hasDrawdownError: ComputedRef<boolean>
  readonly isRecordingPayment: ComputedRef<boolean>
  readonly hasPaymentError: ComputedRef<boolean>
  readonly paymentErrorDetail: ComputedRef<string | undefined>
  recordDrawdown(input: RecordLoanDrawdownInput): Promise<LoanDrawdown>
  recordPayment(input: RecordLoanPaymentInput): Promise<LoanPayment>
  readonly isDeleting: ComputedRef<boolean>
  readonly hasDeleteError: ComputedRef<boolean>
  readonly deleteErrorDetail: ComputedRef<string | undefined>
  deleteLoan(): Promise<void>
  retry(): void
}

export function useLoan(options: UseLoanOptions): UseLoan {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const loanQuery = useQuery({
    queryKey: computed(() => loanKeys.detail(options.ledgerId() ?? '', options.loanId())),
    queryFn: ({ signal }) => api.loans.get(options.ledgerId()!, options.loanId(), signal),
    enabled: active,
    retry: false,
  })

  const loan = computed(() => loanQuery.data.value)
  const loanReady = computed(() => active.value && loan.value !== undefined)

  const principalAccountId = computed(() => loan.value?.principalAccountId)

  const paymentsQuery = useQuery({
    queryKey: computed(() =>
      entryKeys.list(
        options.ledgerId() ?? '',
        principalAccountId.value === undefined ? {} : { accountId: principalAccountId.value },
      ),
    ),
    queryFn: ({ signal }) =>
      api.entries.list(options.ledgerId()!, { accountId: principalAccountId.value! }, signal),
    enabled: loanReady,
    retry: false,
  })

  const through = computed(() =>
    loan.value === undefined
      ? undefined
      : formatLocalDate(plusMonths(loan.value.nextAccrualDate, options.projectionMonths())),
  )

  const projectionsQuery = useQuery({
    queryKey: computed(() =>
      loanKeys.accrualProjections(options.ledgerId() ?? '', options.loanId(), {
        through: through.value ?? '',
      }),
    ),
    queryFn: ({ signal }) =>
      api.loans.accrualProjections(
        options.ledgerId()!,
        options.loanId(),
        { through: through.value! },
        signal,
      ),
    enabled: computed(() => loanReady.value && through.value !== undefined),
    retry: false,
  })

  const accountsTreeQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  function invalidate(): Promise<void> {
    const id = options.ledgerId()
    if (id === undefined) return Promise.resolve()
    return Promise.all(
      loanKeys.invalidatedByWrite(id).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ).then(() => undefined)
  }

  const drawdownMutation = useMutation({
    mutationFn: (input: RecordLoanDrawdownInput) =>
      api.loans.recordDrawdown(options.ledgerId()!, options.loanId(), input),
    onSuccess: async () => {
      await invalidate()
      const id = options.ledgerId()
      if (id === undefined) return
      await Promise.all(
        entryKeys.invalidatedByWrite(id).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      )
    },
  })

  const paymentMutation = useMutation({
    mutationFn: (input: RecordLoanPaymentInput) =>
      api.loans.recordPayment(options.ledgerId()!, options.loanId(), input),
    onSuccess: () => invalidate(),
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.loans.delete(options.ledgerId()!, options.loanId()),
    onSuccess: () => invalidate(),
  })

  return {
    loan,
    payments: computed(() =>
      loan.value === undefined
        ? []
        : paymentRowsFromEntries(paymentsQuery.data.value?.items ?? [], loan.value),
    ),
    hasHistory: computed(() =>
      paymentsQuery.data.value === undefined ? undefined : paymentsQuery.data.value.items.length > 0,
    ),
    projections: computed(() => projectionsQuery.data.value ?? []),
    drawdownAccounts: computed(() =>
      loan.value === undefined
        ? []
        : flattenSourceAccounts(
            accountsTreeQuery.data.value ?? ([] as AccountNode[]),
            loan.value.currency,
            options.locale(),
            ['ASSET'],
          ),
    ),
    sourceAccounts: computed(() =>
      loan.value === undefined
        ? []
        : flattenSourceAccounts(
            accountsTreeQuery.data.value ?? ([] as AccountNode[]),
            loan.value.currency,
            options.locale(),
          ),
    ),
    isLoading: computed(() => active.value && (loanQuery.isPending.value || paymentsQuery.isLoading.value)),
    error: computed(() => loanQuery.error.value ?? paymentsQuery.error.value),
    notFound: computed(() => isNotFound(loanQuery.error.value)),
    retrying: retrying(loanQuery, paymentsQuery),
    isRecordingDrawdown: computed(() => drawdownMutation.isPending.value),
    hasDrawdownError: computed(() => drawdownMutation.error.value !== null),
    drawdownErrorDetail: computed(() => problemDetail(drawdownMutation.error.value)),
    recordDrawdown: (input) => drawdownMutation.mutateAsync(input),
    isRecordingPayment: computed(() => paymentMutation.isPending.value),
    hasPaymentError: computed(() => paymentMutation.error.value !== null),
    paymentErrorDetail: computed(() => problemDetail(paymentMutation.error.value)),
    recordPayment: (input) => paymentMutation.mutateAsync(input),
    isDeleting: computed(() => deleteMutation.isPending.value),
    hasDeleteError: computed(() => deleteMutation.error.value !== null),
    deleteErrorDetail: computed(() => problemDetail(deleteMutation.error.value)),
    deleteLoan: () => deleteMutation.mutateAsync(),
    retry: () => {
      void loanQuery.refetch()
      void paymentsQuery.refetch()
    },
  }
}
